import _ from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import {
  Text,
  Box,
  NumberInput,
  Cell,
  FormField,
  Dropdown,
} from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { DEFAULT_CURRENCY_SYMBOL } from '@utils/constants';
import type { ServicePriceData } from '@common/types';
import { Budget } from '../../CompactBriefPage/CompactBriefPage';
import { BUDGET_ERROR_STATE, BUDGET_STATUS_MAP } from '../CompactBrief';

export type BudgetSectionProps = {
  budget: Budget;
  currencyOptionsArr: Array<any>;
  budgetError: string;
  servicePriceData?: ServicePriceData;
  isMobileView: boolean;
  handleBudgetChange: (val: Partial<Budget>) => void;
  setBudgetQuestionStatus: (val: string) => void;
};
const BudgetSection: FC<BudgetSectionProps> = ({
  budget,
  currencyOptionsArr,
  budgetError,
  servicePriceData,
  isMobileView,
  handleBudgetChange,
  setBudgetQuestionStatus,
}) => {
  const SHOW_BUDGET = 'SHOW_BUDGET';
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [budgetDropdownSelectedValue, setBudgetDropdownSelectedValue] =
    useState('');
  const [budgetInputValue, setBudgetInputValue] = useState(null as any);
  const [budgetDescription, setBudgetDescription] = useState<string>('');

  const handleBudgetDropdownChange = (option: any) => {
    setBudgetDropdownSelectedValue(option.id);
    const status =
      SHOW_BUDGET === option.id
        ? BUDGET_STATUS_MAP.ENTER_A_BUDGET
        : BUDGET_STATUS_MAP.NO_BUDGET;
    setBudgetQuestionStatus(status);
  };

  useEffect(() => {
    handleBudgetChange({
      budgetValue: budgetInputValue,
    });
  }, [budgetInputValue]);

  useEffect(() => {
    if (servicePriceData) {
      const symbol = budget.symbol
        ? budget.symbol.replace('(', '').replace(')', '')
        : DEFAULT_CURRENCY_SYMBOL;
      const description = t('compact-brief-page-questions-budget-description', {
        lowRange: Intl.NumberFormat(language, {
          maximumFractionDigits: 0,
        }).format(servicePriceData.lowRange * budget.conversionRate),
        symbol,
      });
      setBudgetDescription(description);
    } else {
      setBudgetDescription(
        t('compact-brief-page-questions-budget-description-no-service-price'),
      );
    }
  }, [budget.currency, servicePriceData]);

  const budgetDropdownOptions = [
    {
      value: t('brief-modal-budget-option-1'),
      id: SHOW_BUDGET,
    },
    {
      value: t('brief-modal-budget-option-2'),
      id: 'null',
    },
  ];

  return (
    <>
      <FormField
        label={t('brief-modal-budget-title')}
        required
        status={
          budgetError === BUDGET_ERROR_STATE.UNTOUCHED ? 'error' : undefined
        }
        statusMessage={
          budgetError === BUDGET_ERROR_STATE.UNTOUCHED
            ? t('compact-brief-page-questions-budget-error-msg')
            : ''
        }
      >
        <Dropdown
          dataHook="brief-budget-dropdown"
          inContainer
          autoFocus={false}
          autoSelect={false}
          maxHeightPixels="350px"
          placeholder={t('brief-modal-budget-placeholder')}
          options={budgetDropdownOptions}
          selectedId={budgetDropdownSelectedValue}
          onSelect={(option: any) => handleBudgetDropdownChange(option)}
          size="medium"
          disabled={false}
        />
      </FormField>

      {budgetDropdownSelectedValue === SHOW_BUDGET ? (
        <Box direction="vertical" paddingTop={isMobileView ? '0' : '24px'}>
          <Box
            direction="horizontal"
            paddingBottom="6px"
            width={isMobileView ? '100%' : '285px'}
          >
            <Cell span={6}>
              <FormField
                label={t('compact-brief-page-questions-budget-label')}
                status={
                  budgetError === BUDGET_ERROR_STATE.INVALID_BUDGET
                    ? 'error'
                    : undefined
                }
                statusMessage={
                  budgetError === BUDGET_ERROR_STATE.INVALID_BUDGET
                    ? t('compact-brief-page-questions-budget-input-error-msg')
                    : ''
                }
              >
                <NumberInput
                  dataHook="brief-budget-number-input"
                  autoFocus
                  value={budgetInputValue}
                  onChange={(e) => setBudgetInputValue(e ?? undefined)}
                  min={1}
                />
              </FormField>
            </Cell>
            <Cell span={6}>
              <FormField
                label={<div style={{ visibility: 'hidden' }}>Currency</div>}
              >
                <Box paddingLeft="12px">
                  <Dropdown
                    dataHook="brief-budget-currency-dropdown"
                    selectedId={budget.currency}
                    options={currencyOptionsArr}
                    onSelect={(e: any) =>
                      handleBudgetChange({
                        currency: e.id.toString(),
                        symbol: e.value
                          ? e.value.toString().split(' ')[1].toString()
                          : '',
                      })
                    }
                  />
                </Box>
              </FormField>
            </Cell>
          </Box>
          {budgetDescription && (
            <Text
              dataHook="budget-price-range"
              size="small"
              weight="thin"
              secondary
            >
              {budgetDescription}
            </Text>
          )}
        </Box>
      ) : null}
    </>
  );
};

export default BudgetSection;
